/**
 * ODIN session state.
 *
 * One session machine is shared by the whole app through {@link SessionProvider}
 * so that:
 *   - the session is fetched once, not once per mounted component;
 *   - logging out updates every screen at the same instant instead of leaving
 *     stale authenticated data on screens that mounted earlier;
 *   - a logout in one browser tab is picked up by the others.
 *
 * `useSession()` still works without a provider (it falls back to a private
 * machine) so individual components stay unit-testable in isolation.
 *
 * This file is `.ts` rather than `.tsx` on purpose: the provider is built with
 * `createElement` so the module carries no JSX and can be imported from plain
 * TypeScript modules and tests.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api, apiConfigured, type Me } from "../lib/api";

export type SessionState =
  | { kind: "loading" }
  | { kind: "not-configured" }
  | { kind: "unavailable" }
  | { kind: "anonymous" }
  | { kind: "authenticated"; me: Me };

export interface SessionValue {
  state: SessionState;
  /** Re-read the session from the API. */
  refresh: () => void;
  /**
   * End the ODIN session: revoke it server-side, drop every piece of
   * account-derived state held in this browser, and tell the other tabs.
   * Resolves once the local state is anonymous, even if the network call failed.
   */
  logout: () => Promise<void>;
}

/**
 * Cross-tab logout signal. Holds a timestamp only — never session material.
 * `storage` events fire in the *other* tabs of the same origin, which is exactly
 * the notification we need.
 */
const LOGOUT_SIGNAL_KEY = "odin.auth.logged-out-at";

/**
 * Browser-held state that is derived from the signed-in account and must not
 * survive a logout (the next person to use this browser must not see the
 * previous customer's workspace or server names). Cookie-consent preferences
 * belong to the browser, not the account, so they are deliberately not listed.
 */
const ACCOUNT_SCOPED_STORAGE_KEYS = ["odin.workspace-selection.v1"] as const;

function clearAccountScopedStorage(): void {
  try {
    for (const key of ACCOUNT_SCOPED_STORAGE_KEYS) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode) — nothing was persisted to begin with.
  }
}

function broadcastLogout(): void {
  try {
    window.localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
  } catch {
    // Best effort: other tabs still revalidate when they regain focus.
  }
}

/**
 * The actual session machine. `enabled` is false for the fallback instance used
 * when a provider is present, so the fallback never issues a duplicate request.
 * It is constant for the lifetime of a component, so hook order is stable.
 */
function useSessionMachine(enabled: boolean): SessionValue {
  const [state, setState] = useState<SessionState>(
    !enabled || apiConfigured ? { kind: "loading" } : { kind: "not-configured" },
  );
  // Guards against a slow in-flight response overwriting a newer result (most
  // importantly: a `/me` response landing after the user pressed Log out).
  const generation = useRef(0);

  const load = useCallback(() => {
    if (!enabled) return;
    if (!apiConfigured) {
      setState({ kind: "not-configured" });
      return;
    }
    const mine = ++generation.current;
    api
      .session()
      .then(async (s) => {
        if (!s.authenticated) {
          if (generation.current === mine) setState({ kind: "anonymous" });
          return;
        }
        const me = await api.me();
        if (generation.current === mine) setState({ kind: "authenticated", me });
      })
      .catch((err: unknown) => {
        if (generation.current !== mine) return;
        // A rejected identity is "signed out", not "the API is broken" — the
        // difference decides whether the guard redirects or shows an error.
        const status = err instanceof ApiError ? err.status : 0;
        setState(status === 401 || status === 403 ? { kind: "anonymous" } : { kind: "unavailable" });
      });
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = useCallback(async () => {
    // Invalidate any in-flight session request first, so a response that is
    // already on the wire cannot re-authenticate the UI after this point.
    generation.current += 1;
    try {
      await api.logout();
    } catch {
      // The cookie may already be gone, or the API may be unreachable. Either
      // way the local session ends — we must not leave the user looking at an
      // authenticated dashboard.
    }
    clearAccountScopedStorage();
    broadcastLogout();
    setState({ kind: "anonymous" });
  }, []);

  // Revalidate when the tab becomes visible again, and when a page is restored
  // from the back/forward cache. Without this, pressing Back after logging out
  // could re-display a cached authenticated page.
  useEffect(() => {
    if (!enabled) return undefined;
    const revalidate = (): void => {
      if (document.visibilityState === "visible") load();
    };
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) load();
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== LOGOUT_SIGNAL_KEY) return;
      // Another tab logged out. Drop to anonymous immediately, then confirm.
      generation.current += 1;
      setState({ kind: "anonymous" });
      load();
    };
    window.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
    };
  }, [enabled, load]);

  return useMemo<SessionValue>(() => ({ state, refresh: load, logout }), [state, load, logout]);
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

/** Provides one shared session to the whole application. */
export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const value = useSessionMachine(true);
  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): SessionValue {
  const shared = useContext(SessionContext);
  // Constant per component instance: a component is either rendered inside the
  // provider or it isn't, so this does not change hook order between renders.
  const fallback = useSessionMachine(shared === undefined);
  return shared ?? fallback;
}
